import axios from "axios";
import { getToken, clearToken, isTokenExpired } from "./tokenUtils";

const BASE_URL = "https://hrmsnew.prepstripe.com/api";

// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track if we're already processing a logout to prevent multiple triggers
let isLoggingOut = false;
// Track failed requests to prevent logout on transient errors
// Map<requestKey, {count: number, firstFailure: number}>
const failedRequests = new Map();
const MAX_RETRIES = 5; // Increased from 2 to 5 to reduce false logouts
const RETRY_TIMEOUT = 60000; // 1 minute window - reset failure count after this

// Request interceptor - attach token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // Check if token is expired before making request
      if (isTokenExpired()) {
        // Token is expired, but don't logout immediately
        // Let the response interceptor handle it based on actual API response
        console.warn("Token appears to be expired, request may fail");
      }
    }

    // DEBUG: Log all requests to employment-details endpoints
    const url = config?.url || "";
    if (url.includes("employment-details")) {
      console.log("🔵 Making request to employment-details endpoint:", {
        method: config.method,
        url: url,
        fullUrl: config.baseURL + url,
      });
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle 401/403 intelligently
apiClient.interceptors.response.use(
  (response) => {
    // Clear any failed request tracking on success
    const requestKey = `${response.config.method}-${response.config.url}`;
    failedRequests.delete(requestKey);
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const config = error.config;
    const requestKey = `${config?.method}-${config?.url}`;

    // Don't logout on 404 errors (resource not found)
    if (status === 404) {
      // 404 is not an authentication error, just return the error
      return Promise.reject(error);
    }

    // Only handle 401 for actual authentication errors
    if (status === 401) {
      // Check if this request explicitly opted out of auto-logout
      if (config?.skipAuthLogout) {
        console.log(
          "apiUtils: skipAuthLogout flag set - NOT triggering logout",
          {
            url: config?.url,
          },
        );
        return Promise.reject(error);
      }

      // Get URL from multiple sources (config, request, response)
      const url =
        config?.url || error.request?.responseURL || error.config?.url || "";
      const baseURL = config?.baseURL || "";
      const fullUrl = baseURL + url;
      const responseURL =
        error.request?.responseURL || error.response?.config?.url || "";

      // Create a comprehensive string to check against (case-insensitive)
      // Check all possible URL sources
      const allUrls = [url, fullUrl, responseURL, error.request?.responseURL]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      // DEBUG: Log all 401 errors to see what's happening
      console.log("=== 401 ERROR DETECTED ===", {
        url: url,
        baseURL: baseURL,
        fullUrl: fullUrl,
        responseURL: responseURL,
        allUrls: allUrls,
        status: status,
        method: config?.method,
        errorConfig: error.config,
        errorRequest: error.request,
      });

      // Check if this is a campaigns endpoint FIRST - handle separately to prevent premature logout
      // Check multiple variations to catch all cases (must be checked before other endpoints)
      // Use more specific matching to avoid false positives
      const isCampaignsEndpoint =
        (url && (url.includes("/campaigns/") || url.includes("/campaigns"))) ||
        (responseURL &&
          (responseURL.includes("/campaigns/") ||
            responseURL.includes("/campaigns"))) ||
        (fullUrl &&
          (fullUrl.includes("/campaigns/") ||
            fullUrl.includes("/campaigns"))) ||
        (allUrls &&
          (allUrls.includes("/campaigns/") ||
            allUrls.includes("/campaigns"))) ||
        (config?.url &&
          (config.url.includes("/campaigns/") ||
            config.url.includes("/campaigns")));

      // Check if this is an employment-details endpoint - never trigger logout for these
      // These endpoints may have permission issues but shouldn't cause logout
      // Check multiple variations to catch all cases
      const isEmploymentDetailsEndpoint =
        allUrls.includes("employment-details") ||
        allUrls.includes("/v1/employment-details") ||
        url.includes("/employment-details") ||
        url.includes("employment-details") ||
        responseURL.includes("employment-details");

      // For campaigns endpoints, NEVER trigger logout (check this FIRST)
      // These might have permission issues but shouldn't cause immediate logout
      if (isCampaignsEndpoint) {
        console.log("✅ Campaigns endpoint detected - NOT triggering logout:", {
          url: url,
          baseURL: baseURL,
          fullUrl: fullUrl,
          responseURL: responseURL,
          allUrls: allUrls,
          status: status,
          method: config?.method,
          isCampaignsEndpoint: true,
        });
        // Just return the error without tracking or logging out
        // Clear any existing failure tracking for this endpoint
        failedRequests.delete(requestKey);
        return Promise.reject(error);
      }

      // For employment-details endpoints, NEVER trigger logout
      // These may have permission issues but shouldn't cause session termination
      if (isEmploymentDetailsEndpoint) {
        console.log(
          "✅ Employment-details endpoint detected - NOT triggering logout:",
          {
            url: url,
            baseURL: baseURL,
            fullUrl: fullUrl,
            status: status,
            method: config?.method,
            isEmploymentDetailsEndpoint: true,
          },
        );
        // Just return the error without tracking or logging out
        // Clear any existing failure tracking for this endpoint
        failedRequests.delete(requestKey);
        return Promise.reject(error);
      }

      console.log("❌ NOT a special endpoint - may trigger logout:", {
        url: url,
        fullUrl: fullUrl,
        status: status,
      });

      // Check if this is an auth-related endpoint (login, register, me, logout)
      const isAuthEndpoint =
        allUrls.match(/\/login(\s|$|\?)/) ||
        allUrls.match(/\/register(\s|$|\?)/) ||
        allUrls.match(/\/me(\s|$|\?)/) ||
        allUrls.match(/\/logout(\s|$|\?)/);

      // For auth endpoints, logout immediately on 401/403
      if (isAuthEndpoint) {
        if (!isLoggingOut) {
          isLoggingOut = true;
          console.log("apiUtils: Dispatching auth:logout for auth endpoint", {
            url,
            fullUrl,
            responseURL,
          });
          clearToken();
          window.dispatchEvent(new CustomEvent("auth:logout"));
        }
        return Promise.reject(error);
      }

      // For non-auth endpoints, check if we've seen multiple failures
      const now = Date.now();
      const failureData = failedRequests.get(requestKey) || {
        count: 0,
        firstFailure: now,
      };

      // Reset failure count if first failure was more than RETRY_TIMEOUT ago
      if (now - failureData.firstFailure > RETRY_TIMEOUT) {
        failureData.count = 0;
        failureData.firstFailure = now;
      }

      failureData.count += 1;

      // Only logout after multiple consecutive failures on the same endpoint
      // This prevents logout on single permission errors or transient issues
      if (failureData.count >= MAX_RETRIES) {
        // Multiple failures on same endpoint within timeout window, likely token expired
        if (!isLoggingOut) {
          isLoggingOut = true;
          console.log(
            "apiUtils: Dispatching auth:logout due to repeated failures",
            { requestKey, failureData },
          );
          clearToken();
          window.dispatchEvent(new CustomEvent("auth:logout"));
        }
        failedRequests.delete(requestKey); // Clear after logout
        return Promise.reject(error);
      }

      // Track this failed request
      failedRequests.set(requestKey, failureData);

      // For first failure on non-auth endpoint, don't logout immediately
      // Could be a transient error or permission issue
      // Let the error propagate normally
      return Promise.reject(error);
    }

    // For non-401/403 errors, clear tracking
    failedRequests.delete(requestKey);
    return Promise.reject(error);
  },
);

// Reset logout flag and clear failed requests periodically
// This prevents memory leaks and allows retry after logout
setInterval(() => {
  if (isLoggingOut && !getToken()) {
    // Token cleared, reset flag after 5 seconds
    setTimeout(() => {
      isLoggingOut = false;
      failedRequests.clear();
    }, 5000);
  } else if (!isLoggingOut) {
    // Clear old failure tracking (older than 5 minutes) to prevent memory leaks
    // This is handled by the fact that successful requests clear their entries
    // We just need to periodically clean up stale entries
    const now = Date.now();
    // Note: We're not storing timestamps, so we'll just clear all on successful requests
    // This is simpler and sufficient for our use case
  }
}, 10000);

export default apiClient;
